import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Box, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import { Wallpaper, Brightness4, Brightness7 } from '@mui/icons-material';
import { useColorMode } from '../context/ColorModeContext';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import ProfileMenu from './ProfileMenu';
import { authService } from '../services/api';
import { useTranslation } from 'react-i18next';

const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron === true;

const BACKGROUND_PRESETS = [
  '', // Default (none)
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1920&q=80', // Nature
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80', // City
  'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?auto=format&fit=crop&w=1920&q=80', // Mountain
  'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80', // Tech
];

export default function Header({ user, setUser }) {
  const navigate = useNavigate();
  const { toggleColorMode, mode } = useColorMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [bgMenuAnchor, setBgMenuAnchor] = useState(null);
  const { t } = useTranslation();

  const handleLogout = () => {
    localStorage.removeItem('era_token');
    localStorage.removeItem('era_user');
    if (isElectron) {
      localStorage.removeItem('era_desktop_user');
      navigate('/setup');
    } else {
      navigate('/login');
    }
  };

  const handleBackgroundChange = async (url) => {
    try {
      await authService.updateProfile({ dashboard_background: url });
      if (user && setUser) {
          const updatedUser = { ...user, dashboard_background: url };
          setUser(updatedUser);
          localStorage.setItem('era_user', JSON.stringify(updatedUser));
      }
      setBgMenuAnchor(null);
      // Force reload to apply background globally if needed, or rely on state update
      // For now, state update in Layout should trigger re-render
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AppBar 
        position="static" 
        color={user?.dashboard_background ? 'transparent' : 'default'} 
        sx={{ 
            backdropFilter: user?.dashboard_background ? 'blur(10px)' : 'none', 
            boxShadow: user?.dashboard_background ? 'none' : 1,
            mb: 0 // Remove margin bottom as it might be handled by container
        }}
    >
      <Toolbar>
        <Logo sx={{ mr: 2, display: 'flex', cursor: 'pointer', height: { xs: 32, md: 40 } }} onClick={() => navigate('/dashboard')} />

        <Box sx={{ flexGrow: 1 }} />

        <IconButton
            color="inherit"
            sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
            onClick={(e) => setBgMenuAnchor(e.currentTarget)}
        >
            <Wallpaper />
        </IconButton>

        <Menu
            anchorEl={bgMenuAnchor}
            open={Boolean(bgMenuAnchor)}
            onClose={() => setBgMenuAnchor(null)}
        >
            <MenuItem onClick={() => handleBackgroundChange('')}>{t('header.backgroundDefault')}</MenuItem>
            <MenuItem onClick={() => handleBackgroundChange(BACKGROUND_PRESETS[1])}>{t('header.backgroundNature')}</MenuItem>
            <MenuItem onClick={() => handleBackgroundChange(BACKGROUND_PRESETS[2])}>{t('header.backgroundCity')}</MenuItem>
            <MenuItem onClick={() => handleBackgroundChange(BACKGROUND_PRESETS[3])}>{t('header.backgroundMountain')}</MenuItem>
            <MenuItem onClick={() => handleBackgroundChange(BACKGROUND_PRESETS[4])}>{t('header.backgroundTech')}</MenuItem>
        </Menu>

        <IconButton sx={{ ml: 1 }} onClick={toggleColorMode} color="inherit">
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
        </IconButton>

        <LanguageSwitcher />
        {!isElectron && <NotificationBell />}

        <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={Boolean(anchorEl) ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
        >
            <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                src={user?.avatar}
            >
                {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
        </IconButton>

        <ProfileMenu 
            anchorEl={anchorEl}
            handleClose={() => setAnchorEl(null)}
            handleLogout={handleLogout} 
            user={user}
            toggleColorMode={toggleColorMode}
            mode={mode}
            setAnchorEl={setAnchorEl}
        />
      </Toolbar>
    </AppBar>
  );
}
